import { Check, Copy } from 'lucide-react'
import React, { useState } from 'react'

import OpacityTransition from '../reusable/OpacityTransition'

// CopyButtonWithAnimation is a button that copies the content of a search result to the clipboard.
// It displays a checkmark icon when the content is copied.
const CopyButtonWithAnimation = ({
  content,
  children,
}: {
  content: string
  children: React.ReactNode
}) => {
  const [isCopied, setIsCopied] = useState(false)

  const handleCopy = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    // Copy text to clipboard
    navigator.clipboard.writeText(content)

    // Trigger the animation
    setIsCopied(true)

    // Hide the icon after 2 seconds
    setTimeout(() => setIsCopied(false), 2000)
  }

  return (
    <div
      className='cursor-pointer ml-2'
      onClick={handleCopy}
      style={{ position: 'relative' }}
    >
      {isCopied
        ? (
          <OpacityTransition key='copy'>
            <Check size={16} stroke='#22C55E' />
          </OpacityTransition>
          )
        : (
          <OpacityTransition key='check'>{children}</OpacityTransition>
          )}
    </div>
  )
}

// Custom copy icon component that conforms to IconProp interface
export const CopyButtonWithAnimationActionIcon = ({
  'aria-hidden': ariaHidden,
  size = 12,
}: {
  'aria-hidden'?: boolean | 'true' | 'false'
  size?: string | number
}) => {
  const [isCopied, setIsCopied] = useState(false)

  // Trigger animation when clicked.
  // This version doesn't stop propagation to work with Badge's onActionClick.
  const handleClick = () => {
    // Don't stop propagation - let parent components handle the actual copy logic.
    setIsCopied(true)
    setTimeout(() => setIsCopied(false), 2000)
  }

  // When used as actionIcon, Badge wraps this in a button, so we add our own click handler
  // that doesn't interfere with the Badge's button click handler.
  return (
    <span onClick={handleClick}>
      {isCopied
        ? (
          <OpacityTransition key='copy'>
            <Check size={size} stroke='#22C55E' aria-hidden={ariaHidden} />
          </OpacityTransition>
          )
        : (
          <OpacityTransition key='check'>
            <Copy size={size} aria-hidden={ariaHidden} />
          </OpacityTransition>
          )}
    </span>
  )
}

export default CopyButtonWithAnimation
